import React, { useState } from "react";
import { IoMdClose } from "react-icons/io";
import { API_URL } from "../../config/apiConfig";
import arrow from "../../images/home/white-Arrow.svg";

const DocumentModal = ({ isOpen, onClose, document }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "", 
    email: "",
    phone: "",
    city: "",
    state: ""
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Name validation
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = "First name must be at least 2 characters";
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required"; 
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = "Last name must be at least 2 characters";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = "Email address is required";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Phone validation
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!phoneRegex.test(formData.phone.replace(/\s|-/g, ""))) {
      errors.phone = "Please enter a valid phone number";
    }

    // City validation
    if (!formData.city.trim()) {
      errors.city = "City is required";
    } else if (formData.city.trim().length < 2) {
      errors.city = "City must be at least 2 characters";
    }

    // State validation
    if (!formData.state.trim()) {
      errors.state = "State is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_URL}/api/send-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          documentName: document.name,
          documentFilename: document.filename
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          city: "",
          state: ""
        });
        
        // Auto close after success
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 3000);
      } else {
        const errorData = await response.json();
        setSubmitStatus('error');
        console.error('Form submission error:', errorData);
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Network error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      city: "",
      state: ""
    });
    setFormErrors({});
    setSubmitStatus(null);
    onClose();
  };

  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md lg:max-w-2xl xl:max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200">
          <div>
            <h3 className="hellix-font text-xl font-bold text-blue-900 mb-2">
              Download Document
            </h3>
            <p className="hellix-font text-sm text-gray-600">
              {document.name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <IoMdClose size={24} />
          </button>
        </div>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className="m-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="hellix-font text-sm text-green-800 font-medium">
                  Document sent successfully!
                </p>
                <p className="hellix-font text-xs text-green-600 mt-1">
                  Please check your email for the download link.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="hellix-font text-sm text-red-800 font-medium">
                Failed to send document. Please try again.
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 lg:p-8">
          <p className="hellix-font text-sm lg:text-base text-gray-600 mb-6 lg:mb-8">
            Please provide your details to receive the document via email.
          </p>

          {/* Name Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 lg:mb-6">
            <div>
              <label className="block hellix-font text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg hellix-font focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  formErrors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="John"
              />
              {formErrors.firstName && (
                <p className="hellix-font text-xs text-red-500 mt-1">{formErrors.firstName}</p>
              )}
            </div>
            
            <div>
              <label className="block hellix-font text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg hellix-font focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  formErrors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Smith"
              />
              {formErrors.lastName && (
                <p className="hellix-font text-xs text-red-500 mt-1">{formErrors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email and Phone - Side by side on larger screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 lg:mb-6">
            {/* Email */}
            <div>
              <label className="block hellix-font text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg hellix-font focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  formErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="john.smith@example.com"
              />
              {formErrors.email && (
                <p className="hellix-font text-xs text-red-500 mt-1">{formErrors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block hellix-font text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg hellix-font focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  formErrors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="+1 234 567 8900"
              />
              {formErrors.phone && (
                <p className="hellix-font text-xs text-red-500 mt-1">{formErrors.phone}</p>
              )}
            </div>
          </div>

          {/* Location Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 lg:mb-8">
            <div>
              <label className="block hellix-font text-sm font-medium text-gray-700 mb-2">
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg hellix-font focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  formErrors.city ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Sydney"
              />
              {formErrors.city && (
                <p className="hellix-font text-xs text-red-500 mt-1">{formErrors.city}</p>
              )}
            </div>
            
            <div>
              <label className="block hellix-font text-sm font-medium text-gray-700 mb-2">
                State *
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg hellix-font focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  formErrors.state ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="e.g. NSW, Victoria, California"
              />
              {formErrors.state && (
                <p className="hellix-font text-xs text-red-500 mt-1">{formErrors.state}</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6 border-t border-gray-200 flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`relative text-center justify-center group hellix-font font-bold text-[16px] h-[50px] xl:h-[56px] rounded-[28px] flex items-center space-x-2 border-2 overflow-hidden transition-all duration-300 ${
                isSubmitting
                  ? 'w-[180px] bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300'
                  : 'w-[180px] xl:w-[200px] text-[#004b6b] border-blue'
              }`}
            >
              {/* Background Animation */}
              <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-all duration-1000 ease-in-out origin-left z-0"></span>

              {isSubmitting ? (
                <div className="flex items-center justify-center relative z-10">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </div>
              ) : (
                <>
                  {/* Button Content */}
                  <span className="relative z-10 group-hover:text-blue transition-colors duration-700 ease-in">
                    Send Document
                  </span>

                  <img
                    src={arrow}
                    alt="Arrow Icon"
                    className="relative z-10 transition-all duration-500 ease-in group-hover:ml-3 group-hover:filter group-hover:invert group-hover:sepia group-hover:saturate-200 group-hover:hue-rotate-0 group-hover:contrast-105 w-[14px]"
                  />
                </>
              )}
            </button>
          </div>

          <p className="hellix-font text-xs text-gray-500 mt-4 text-center">
            By submitting this form, you agree to our Privacy Policy and Terms of Service.
          </p>
        </form>
      </div>
    </div>
  );
};

export default DocumentModal;